import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Minus, Plus, Trash2 } from 'lucide-react';
import { formatMoney } from '../format';

const TRANSLATION = 'translation';
const MANUAL = 'manual';

// A line keeps the price it was quoted at, whatever the catalogue does later.
export default function QuoteBuilder({
  services,
  charsPerUnit,
  items,
  quoteTotal,
  orderTotal,
  source,
  manualDraft,
  priceListUrl,
  onItemsChange,
  onManualDraftChange,
  onManualCommit,
  onUseQuote,
}) {
  const { t, i18n } = useTranslation('common');
  // Saves on blur, so the "1" of a half-typed "12" never reaches the server.
  const [editing, setEditing] = useState(null);

  const byKey = useMemo(
    () => Object.fromEntries(services.map((service) => [service.key, service])),
    [services],
  );

  const groups = useMemo(() => {
    const sorted = [...services].sort((a, b) =>
      a.labels[i18n.language].localeCompare(b.labels[i18n.language], i18n.language),
    );
    return {
      translation: sorted.filter((service) => service.kind === TRANSLATION),
      extra: sorted.filter((service) => service.kind !== TRANSLATION),
    };
  }, [services, i18n.language]);

  const unitLabel = (service) =>
    service.unit === 'chars'
      ? t('crm.quote.perChars', { chars: charsPerUnit })
      : t('crm.quote.perDocument');

  const money = (value) => formatMoney(value, i18n.language);

  const option = (service) => (
    <option key={service.key} value={service.key}>
      {service.labels[i18n.language]} · {money(service.price)} {t('crm.currency')} {unitLabel(service)}
    </option>
  );

  const add = (key) =>
    onItemsChange([...items, { service_key: key, quantity: 1, unit_price: byKey[key].price }]);

  const commitQuantity = (index) => {
    const quantity = Math.max(1, Math.round(Number(editing.value)) || 1);
    setEditing(null);
    if (quantity !== items[index].quantity) {
      onItemsChange(items.map((item, at) => (at === index ? { ...item, quantity } : item)));
    }
  };

  const step = (index, by) => {
    const quantity = Math.max(1, items[index].quantity + by);
    if (quantity !== items[index].quantity) {
      onItemsChange(items.map((item, at) => (at === index ? { ...item, quantity } : item)));
    }
  };

  const remove = (index) => onItemsChange(items.filter((_, at) => at !== index));

  return (
    <>
      <div className="crm-card-head">
        <h2>{t('crm.quote.title')}</h2>
        {priceListUrl && (
          <a className="crm-link" href={priceListUrl} target="_blank" rel="noopener noreferrer">
            {t('crm.lead.priceList')}
            <ExternalLink size={13} />
          </a>
        )}
      </div>

      <label className="crm-field">
        <span className="crm-field-label">{t('crm.quote.addService')}</span>
        <select className="crm-input crm-quote-add" value="" onChange={(event) => add(event.target.value)}>
          <option value="" disabled>
            {t('crm.quote.pick')}
          </option>
          <optgroup label={t('crm.quote.groupTranslation')}>{groups.translation.map(option)}</optgroup>
          <optgroup label={t('crm.quote.groupExtra')}>{groups.extra.map(option)}</optgroup>
        </select>
      </label>

      {items.length === 0 ? (
        <p className="crm-empty-line">{t('crm.quote.empty')}</p>
      ) : (
        <ul className="crm-quote-lines">
          {items.map((item, index) => {
            // A language dropped from the price list still has to show its old quote.
            const service = byKey[item.service_key];
            return (
              <li key={`${item.service_key}-${index}`}>
                <span className="crm-quote-name">
                  <strong>{service ? service.labels[i18n.language] : item.service_key}</strong>
                  <span className="crm-cell-sub">
                    {money(item.unit_price)} {t('crm.currency')} {service ? unitLabel(service) : ''}
                  </span>
                </span>
                <span className="crm-stepper">
                  <button
                    type="button"
                    className="crm-step"
                    aria-label={t('crm.quote.less')}
                    disabled={item.quantity <= 1}
                    onClick={() => step(index, -1)}
                  >
                    <Minus size={14} />
                  </button>
                  <input
                    className="crm-input crm-quote-qty"
                    type="number"
                    min="1"
                    step="1"
                    value={editing && editing.index === index ? editing.value : item.quantity}
                    aria-label={t('crm.quote.quantity')}
                    onChange={(event) => setEditing({ index, value: event.target.value })}
                    onBlur={() => commitQuantity(index)}
                    onKeyDown={(event) => event.key === 'Enter' && event.target.blur()}
                  />
                  <button
                    type="button"
                    className="crm-step"
                    aria-label={t('crm.quote.more')}
                    onClick={() => step(index, 1)}
                  >
                    <Plus size={14} />
                  </button>
                </span>
                <span className="crm-quote-line-total">{money(item.quantity * item.unit_price)}</span>
                <button
                  type="button"
                  className="crm-icon-btn crm-quote-remove"
                  aria-label={t('crm.quote.remove')}
                  onClick={() => remove(index)}
                >
                  <Trash2 size={16} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="crm-quote-total">
        <span className="crm-quote-total-label">
          {t('crm.quote.total')}
          {source && (
            <span className={`crm-badge${source === MANUAL ? ' manual' : ''}`}>
              {t(source === MANUAL ? 'crm.quote.sourceManual' : 'crm.quote.sourceQuote')}
            </span>
          )}
        </span>
        <span className="crm-quote-total-value">
          {orderTotal === null ? '—' : money(orderTotal)}
          <span className="crm-quote-currency">{t('crm.currency')}</span>
        </span>
      </div>

      <div className="crm-quote-manual">
        <label className="crm-field">
          <span className="crm-field-label">{t('crm.quote.manual')}</span>
          <input
            className="crm-input"
            type="number"
            min="0"
            step="0.01"
            value={manualDraft}
            placeholder={t('crm.quote.manualHint')}
            onChange={(event) => onManualDraftChange(event.target.value)}
            onBlur={onManualCommit}
          />
        </label>
        {source === MANUAL && items.length > 0 && (
          <button type="button" className="crm-btn" onClick={onUseQuote}>
            {t('crm.quote.useQuote', { value: money(quoteTotal) })}
          </button>
        )}
      </div>
    </>
  );
}
